'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import type { MissionType } from '@/lib/mission-type';
import type { CooperativeTraffic } from '@/components/dashboard/DroneMap';
import type { LiveDockStatus, DockEventPayload, DockAckPayload } from '@/types/dock';
import type {
  DroneAckPayload,
  DroneEventPayload,
  ManualControlHold,
  ManualControlStatePayload,
} from '@/types/coordination';
import type { CameraLens, CompanionStatus } from '@/types/fleet';
import { api } from '@/lib/api';

/**
 * Resolve the WebSocket URL.
 *
 * Order of precedence:
 *  1. NEXT_PUBLIC_WS_URL set at build time (non-empty).
 *  2. In the browser on a non-dev host: derive from window.location → wss://<host>/ws
 *     (Nginx proxies /ws to the API container). Survives tunnel/host swaps without a rebuild.
 *  3. Dev fallback: ws://localhost:4001 (prod-port host) or :4101 (dev-port host).
 */
export function getWsUrl(): string {
  const fromEnv = process.env.NEXT_PUBLIC_WS_URL;
  if (fromEnv && fromEnv.length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host;
    const isProdLocalHost = host === 'localhost:3000' || host.startsWith('127.0.0.1:3000');
    const isDevLocalHost = host === 'localhost:3002' || host.startsWith('127.0.0.1:3002');
    if (isProdLocalHost) {
      return 'ws://localhost:4001';
    }
    if (isDevLocalHost) {
      return 'ws://localhost:4101';
    }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${host}/ws`;
  }
  return 'ws://localhost:4001';
}

/**
 * One aircraft as the operator screens see it.
 *
 * This is the server's `TelemetryPoint`, stored verbatim from the WebSocket
 * frame (see the `telemetry` branch of the message handler below) with two
 * client-derived fields, `status` and `last_seen_at`, stamped on arrival. Every
 * field the pipeline publishes is therefore already present at runtime; the
 * declarations below are what makes each one usable rather than what makes it
 * arrive.
 *
 * `undefined` on any optional field means the aircraft did not report it. It
 * must be rendered as such and never coerced to zero — the backend stopped
 * substituting placeholders precisely so this distinction survives.
 */
export interface LiveDrone {
  drone_id: string;
  org_id: string;
  callsign?: string;
  latitude: number;
  longitude: number;
  altitude: number;
  /** Above mean sea level, where the aircraft reports it. AGL is `altitude`;
   *  published airspace floors and ceilings are MSL, so both are needed.
   *  NULL when the source does not report it - the MAVLink JSON bridge sends
   *  neither an MSL altitude nor a velocity triple. Test with `== null`, never
   *  `=== undefined`: the wire carries an explicit null, and `null === undefined`
   *  is false, so an undefined-only guard falls through to the value branch. */
  altitude_msl?: number | null;
  speed: number;
  /** Positive is climbing. Null when not reported - see altitude_msl. */
  vertical_speed?: number | null;
  heading: number;
  /** Null when the aircraft reports no charge (BATT_MONITOR unconfigured). */
  battery_percent: number | null;
  battery_voltage?: number;
  battery_temperature?: number;
  /** Amps being drawn, and cumulative mAh out of the pack this flight. */
  battery_current_a?: number;
  battery_consumed_mah?: number;
  /** Always present: the MAVLink adapter falls back to 100 and stamps
   *  telemetry_source so the client suppresses it as "not reported". */
  signal_strength: number;
  satellites: number;
  /** e.g. '3D_FIX', 'RTK_FIXED' — the fix class behind the satellite count. */
  gps_fix_type?: string;
  gps_hdop?: number;
  gps_vdop?: number;
  status: string;
  last_seen_at: string;
  telemetry_source?: 'remoteid' | 'dji_cloud' | 'mavlink' | 'mqtt' | 'simulator';
  /** Decoded autopilot flight mode, e.g. 'AUTO', 'GUIDED', 'RTL'. Absent when
   *  the source does not report one or the mode could not be decoded. */
  flight_mode?: string;
  armed?: boolean;
  /** Percentage of packets the autopilot reports as dropped. This is packet
   *  loss, not RSSI — MAVLink carries no RSSI in the messages we decode, so a
   *  MAVLink aircraft has this and no `signal_strength`. */
  comm_drop_rate?: number;
  comm_errors?: number;
  /** Degrees; positive roll is right wing down, positive pitch is nose up. */
  roll_deg?: number;
  pitch_deg?: number;
  /** Camera gimbal attitude in degrees (issue #244): pitch negative = looking
   *  down, yaw relative to the airframe nose. Absent = not reported — render
   *  "—", never 0, which would claim a camera pose nothing measured. */
  gimbal_pitch?: number;
  gimbal_yaw?: number;
  /** Camera zoom ratio, 1.0 = no zoom. Absent = not reported — render "—". */
  zoom_ratio?: number;
  /** Which lens is feeding the video stream. Absent = not reported — render
   *  "—", and in particular never assume 'wide': an unreported lens and a
   *  confirmed wide lens are different facts, and the lens keys distinguish
   *  "you asked for this" from "the camera says it is this" on exactly that. */
  camera_lens?: CameraLens;
  /** The aircraft's OWN answer to "am I airborne" (DJI KeyIsFlying), as opposed
   *  to the altitude-derived `status`. Absent = the source cannot answer, which
   *  is most of them - never read absence as "on the ground". */
  is_flying?: boolean;
  /** DJI LandingProtectionState by its enum name (NOT_SAFE_TO_LAND,
   *  ANALYSIS_FAILED, ANALYZING, SAFE_TO_LAND, NONE, UNKNOWN). Why a commanded
   *  LAND is hanging at ~1 m (#200). Absent = not reported. */
  landing_protection?: string;
  /**
   * ArduPilot MISSION_CURRENT item index, forwarded verbatim from the MAVLink
   * bridge telemetry when present. Drives the live mission-progress overlay.
   * Item 0 = home, 1 = takeoff, operator waypoint K = mission item K + 2.
   */
  mission_current?: number;
}

/** What happened to a mission. `deleted` is the only kind that carries no row. */
export type MissionEventKind = 'created' | 'updated' | 'deleted';

/**
 * One mission as the dispatch board renders it — structurally the board's own
 * `MissionListItem`, which is what makes a row replaceable from the frame alone with no
 * follow-up fetch. The board asserts that equivalence at compile time when it applies an
 * event, so a server-side change to the shape cannot drift past a type-check.
 *
 * THAT LAST SENTENCE WAS NOT TRUE FOR THE TWO COVERAGE FIELDS, and #420 is what it cost. They
 * are OPTIONAL on `MissionListItem`, so a frame that carried neither still satisfied the
 * assignment — and `applyMissionEvent` replaced live rows with it, stripping both. A type that
 * is structurally assignable is not a type that is complete.
 *
 * AND DO NOT READ THE NON-OPTIONAL DECLARATION BELOW AS A GUARD ON THIS SIDE OF THE WIRE. On the
 * server it is one — `MissionEventRow` has a single construction site, which now cannot omit the
 * key. Here it enforces nothing: `client/tsconfig.json` excludes `__tests__`, vitest transpiles
 * without type-checking, and the frame arrives as an unchecked `as MissionEventPayload` cast in
 * this file. What actually stops the strip is `withOmittedFieldsCarriedOver` in `applyMissionEvent`.
 */
export interface MissionEventMission {
  id: string;
  name: string;
  description: string | null;
  status: string;
  /** 022. On the frame so a mission arriving over the socket renders its type immediately. */
  type: MissionType;
  scheduled_at: string | null;
  estimated_duration_minutes: number | null;
  default_altitude: number;
  default_speed: number;
  total_distance_meters: number | null;
  /** 022/#700. An orbit's radius and direction are its flight path; a row without them reads as a GO TO POINT. */
  orbit_radius_m: number | null;
  orbit_direction: 'cw' | 'ccw' | null;
  drone_callsign: string | null;
  pilot_name: string | null;
  site_name: string | null;
  waypoint_count: number;
  assigned_drone_id: string | null;
  assigned_pilot_id: string | null;
  site_id: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Coverage is attached (#391). `undefined` means the server's lookup FAILED and this frame
   * has no opinion — `applyMissionEvent` keeps the row's previous value. `false` is a real
   * answer and does replace.
   */
  coverage_attached: boolean | undefined;
  /** Coverage is holding it open with nothing in the air (#400). Same `undefined` contract. */
  coverage_held_open: boolean | undefined;
}

/**
 * Where a launch has got to (#418).
 *
 * Mirrors `LaunchStatusRecord` in `src/services/launch-status.ts` FIELD FOR FIELD. Frames arrive
 * as an unchecked cast, so TypeScript catches nothing here if the two drift - see the
 * `RotationReliefPayload` note for the release where a payload nested one level deeper than the
 * server sent it and the socket carried nothing for weeks.
 */
export type LaunchPhase =
  | 'accepted'
  | 'bay_opening'
  | 'awaiting_home_point'
  | 'dispatching'
  | 'dispatched'
  | 'refused';

/**
 * Body of a `launch_event` frame - a dock launch moved to a new phase.
 *
 * A separate frame from `mission_event` because a launch in progress has no new mission row to
 * carry: the mission went `in_progress` before the bay was commanded, and nothing after that
 * changes what the missions board renders. What is moving is the launch, which is not a row
 * anywhere.
 */
export interface LaunchEventPayload {
  launch_id: string;
  org_id: string;
  mission_id: string | null;
  drone_id: string;
  dock_id: string | null;
  phase: LaunchPhase;
  /** The operator's line for why this launch is still going, or null when the phase says it all. */
  detail: string | null;
  /**
   * Set only on `refused`. Usually a launch-service refusal code, but the ad-hoc dock door has
   * its own vocabulary - treat an unrecognised value as "refused, reason in `detail`".
   */
  refusal_code: string | null;
  advisories: string[];
  started_at: string;
  updated_at: string;
}

/** Body of a `mission_event` frame — one mission created, changed or deleted in this org. */
export interface MissionEventPayload {
  mission_id: string;
  org_id: string;
  kind: MissionEventKind;
  /** When the server announced the change (ISO 8601, UTC). */
  occurred_at: string;
  /** The mission after the change; null on `deleted`. */
  mission: MissionEventMission | null;
}

/**
 * Body of a `coverage_event` frame — a continuous-coverage mission's station changed hands, was
 * taken, or was lost (#249).
 *
 * A separate frame from `mission_event` because a coverage transition has no new mission row to
 * carry: the mission itself did not change, only who is holding its station. Carries the whole
 * answer rather than a ping, so the panel repaints without a fetch.
 */
/**
 * An external trigger fired - today, a dock's person detection.
 *
 * Mirrors the server's construction site FIELD FOR FIELD
 * (src/services/airscort-adapter.ts, `evaluatePersonDetection`). Frames arrive
 * as an unchecked cast, so TypeScript catches nothing here - see the
 * RotationReliefPayload note above for the PR where a payload nested one level
 * deeper than the server sent it and the socket carried nothing for weeks.
 */
export interface PersonDetectedPayload {
  /** The `trigger_events` row. Null when the row could not be stored - the
   *  detection still reaches the operator, it just cannot be acted on. */
  trigger_id: string | null;
  dock_id: string;
  score: number;
  /** Null when the dock's GPS_VALID flag was clear. No position, no map point. */
  latitude: number | null;
  longitude: number | null;
  occurred_at: string;
}

/**
 * The platform proposed an aircraft for a trigger, or withdrew a proposal it had made (#253).
 *
 * `kind` is synthesised from the frame TYPE rather than read out of the row, because the row
 * looks the same either way once the proposal columns are cleared - a withdrawn proposal is a
 * row with nothing on it, which is indistinguishable from a trigger that was never decided about.
 */
export interface TriggerProposalPayload {
  kind: 'proposed' | 'voided';
  id: string;
  org_id: string;
  status: string;
  proposed_drone_id: string | null;
  proposed_altitude_m: number | null;
  proposal_mode: 'propose' | 'full_auto' | null;
  proposal_expires_at: string | null;
  /**
   * Every aircraft weighed, and why each was passed over.
   *
   * NOT OPTIONAL, and the reason is a defect this field was added to fix. The card resolves the
   * proposed aircraft's CALLSIGN out of this list - it is the only place the name appears - so a
   * frame without it rendered "Send an aircraft to hold at 60 m" with no name, no justification
   * and an Approve button, for the whole 30 s until the next poll filled it in. That window is
   * exactly when a fresh red point gets clicked, and propose-and-approve's entire premise is that
   * a human approves the NAMED airframe.
   */
  proposal_considered: TriggerConsideredWire[] | null;
  /** Why the platform's last attempt produced no sortie, when it produced none. */
  dispatch_refusal: string | null;
}

/** One aircraft the selector weighed. Mirrors `TriggerConsideredEntry` on the server. */
export interface TriggerConsideredWire {
  droneId: string;
  name: string;
  callsign: string | null;
  rejection: string | null;
  detail: string | null;
}

/** A trigger was answered or cleared - by anyone, on any COP. */
export interface TriggerResolvedPayload {
  id: string;
  org_id: string;
  status: 'dispatched' | 'dismissed';
  mission_id: string | null;
}

export interface CoverageEventPayload {
  mission_id: string;
  org_id: string;
  kind: 'coverage_update';
  occurred_at: string;
  state: 'pending_station' | 'covered' | 'handover' | 'gap' | 'suspended' | 'stopped';
  covered_by_drone_id: string | null;
  covered_by_callsign: string | null;
  /** Why the station is not manned, in the server's words. Null while covered. */
  reason: string | null;
}

/**
 * The replacement forecast, mirroring `RotationSchedulePayload` on the server (#250 W6, plan §14).
 *
 * ADVISORY. Nothing on this frame has been commanded. Since stage 4 W2 the planner NAMES a relief
 * aircraft, but naming is not commanding: no dock is prepared and no aircraft is told anything
 * until an operator approves. `commit_state: 'committed'` means the forecast is inside the freeze
 * horizon and may only move EARLIER — it is not a readiness claim.
 */
export interface RotationSchedulePayload {
  decision: 'scheduled' | 'launch_now' | 'refused' | 'not_planned';
  reason: string | null;
  launch_at: string | null;
  outgoing_must_leave_at: string | null;
  commit_deadline: string | null;
  commit_state: 'tentative' | 'committed' | null;
  /** "Why 14:32" must also answer "and according to whom" (§6.1 rule 2). */
  margin_source: 'dji' | 'derived' | null;
  margin_rejected_reason: string | null;
  outgoing_drone_id: string | null;
  outgoing_callsign: string | null;
  breakdown: Record<string, unknown> | null;
  /**
   * The inputs the planner RESOLVED before the arithmetic ran — endurance and its provenance, the
   * observed drain, cruise speed and its provenance, the battery and reserve, the relief dock.
   *
   * A SIBLING of `breakdown`, matching the server, and it must be read as one: `breakdown` is the
   * pure estimator's own output and knows nothing about where its arguments came from. Reading
   * this out of `breakdown` renders every provenance row as a confident "no energy profile for
   * this airframe" for an aircraft that has one.
   */
  resolved: Record<string, unknown> | null;
}

/**
 * Who relieves the station (stage 4 W2) — a THIRD independently nullable half of the FRAME.
 *
 * **It was declared inside `RotationSchedulePayload` until stage 4 W3, and it is not there on the
 * wire.** The server has always put it at the top level of `RotationEventPayload`, beside
 * `schedule` and `safety`, precisely because the two have different lifetimes and the relief is
 * broadcast ALONE when only it has changed. Nested here, `schedule.relief` was `undefined` on
 * every frame, so the socket path never carried a relief at all — and the relief-only refresh
 * frame, which has `schedule: null`, was dropped whole by the panel's handler. Nothing caught it:
 * every relief test on the panel goes through the 15 s poll, which reads `inputs.relief` off the
 * row and is a different code path.
 *
 * Two different absences, and a surface must not conflate them:
 *   `relief: null`          there is no forecast at all (a `not_planned` frame).
 *   `relief.drone_id: null` there IS a forecast and nobody can fly it — §13's COVERAGE AT RISK.
 *                           The outgoing aircraft must still leave at the published time, so the
 *                           time is real and only the relief is missing.
 */
export interface RotationReliefPayload {
  drone_id: string | null;
  callsign: string | null;
  rotation_order: number | null;
  dock_id: string | null;
  /** Named, and non-null exactly when `drone_id` is null. */
  unavailable_reason: string | null;
  rejected: Array<{
    drone_id: string;
    callsign: string | null;
    rotation_order: number;
    rejection: string;
    reasons: string[];
  }>;
}

/**
 * An operator's approval of a named proposal, and whether it still holds (stage 4 W3).
 *
 * A FOURTH independently nullable half, written the instant somebody clicks rather than on the
 * planner's sweep. `state` is DERIVED on the server by one function and sent whole — the client
 * must never recompute it from the four `approved_*` values, because a panel and a launcher
 * disagreeing about whether an aircraft may fly is the failure the single implementation exists
 * to prevent.
 */
export interface RotationApprovalPayload {
  action_id: string;
  /**
   * `approved` — a human said yes and both facts still match.
   * `void` — they said yes and the proposal has since changed; nothing is armed.
   * `none` — nobody has said anything.
   */
  state: 'none' | 'approved' | 'void';
  /** Why it is void, in the operator's words. Null unless `state` is `void`. */
  void_reason: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  approved_drone_id: string | null;
  approved_callsign: string | null;
  approved_planned_at: string | null;
}

/** A named safety state from the layer that runs whether or not the planner does (§8, §13). */
export interface RotationSafetyPayload {
  state: 'critical' | 'unknown_battery' | 'dji_rth_takeover';
  /**
   * TRUE when the condition has ENDED rather than begun.
   *
   * The server latches, so the entry is the only frame a surface would otherwise ever get — and a
   * red BATTERY CRITICAL with no lifetime outlives the aircraft that earned it, sitting beside a
   * green COVERED badge for its replacement with no way to dismiss it.
   */
  cleared: boolean;
  drone_id: string;
  callsign: string;
  message: string;
  battery_percent: number | null;
  battery_critical_pct: number;
  critical_action: 'alert_only' | 'auto_rth';
  takeover_status: string | null;
  /** THREE-STATE. `null` means DJI published nothing, which is NOT `false`. */
  should_go_home: boolean | null;
}

/**
 * Body of a `rotation_event` frame.
 *
 * The two halves are independently nullable because they have two producers on two clocks: the
 * planner publishes a schedule only when the stability layers let it, and the safety layer
 * publishes the moment a threshold is crossed — deliberately without waiting for the planner,
 * which may be disabled entirely. Apply whichever half arrived and leave the other standing.
 */
export interface RotationEventPayload {
  mission_id: string;
  org_id: string;
  kind: 'rotation_update';
  occurred_at: string;
  schedule: RotationSchedulePayload | null;
  safety: RotationSafetyPayload | null;
  /** Broadcast alone when only the relief has changed — see `RotationReliefPayload`. */
  relief: RotationReliefPayload | null;
  /** Broadcast alone the moment an operator approves or aborts — see `RotationApprovalPayload`. */
  approval: RotationApprovalPayload | null;
  /** Broadcast alone by the orchestrator as the launch progresses — see `RotationPhasePayload`. */
  phase: RotationPhasePayload | null;
}

/**
 * WHERE THE AIRCRAFT HAS GOT TO (stage 4 W4, plan §9), and §13's named state when there is one.
 *
 * DECLARED AT THE TOP LEVEL, beside `schedule` / `safety` / `relief` / `approval`, and it must stay
 * there. This is the exact shape W3 got wrong for the relief: `RotationReliefPayload` was declared
 * INSIDE `RotationSchedulePayload` while the server had always sent it at the top level, so
 * `schedule.relief` was `undefined` on every frame and the socket carried no relief at all for a
 * whole PR. Nothing caught it, because every relief test went through the poll.
 *
 * The same trap is open here and worse: the orchestrator broadcasts a phase-ONLY frame
 * (`schedule: null`), so a nested declaration would drop every phase update on the floor —
 * silently, while an aircraft was leaving a pad.
 */
export interface RotationPhasePayload {
  /** The `rotation_actions` row this is about. Match it against the action being rendered. */
  action_id: string;
  /**
   * W8's last three (#510) are three different situations, not one repeated.
   *
   * `handover_done` is NOT the end: ownership has moved and the relief is holding the station from
   * the handover slot, one separation above where it belongs, while the outgoing aircraft clears
   * the area. `descending` is the relief being flown down. `descent_complete` is the end.
   */
  phase: 'scheduled' | 'preparing' | 'launching' | 'transit'
    | 'on_station' | 'confirming' | 'handover_done'
    | 'descending' | 'descent_complete'
    // #564. A rotation of ONE, which has no relief and therefore no handover: `returning` is the
    // sole aircraft flying home with the station knowingly empty behind it, `resupplying` is it
    // down in its own dock being swapped or charged. The station is UNCOVERED in both.
    | 'returning' | 'resupplying';
  /**
   * §13's named state, or null when nothing needs saying.
   *
   * `approval_required` is the one that is NOT a failure — it means the launch is frozen and the
   * platform is waiting for a human to approve it. `bay_hold` is the same thing about a machine
   * (#547): the launch is frozen because the relief's bay is mid-swap or its pack is still
   * charging, and it clears by itself. `bay_hold_expired` is that wait being given up, and IS a
   * failure.
   */
  phase_reason:
    | 'approval_required' | 'bay_hold' | 'bay_hold_expired'
    | 'replacement_refused' | 'launch_failed_bay' | 'replacement_lost_enroute' | 'launch_unresolved'
    | 'handover_unconfirmed' | 'separation_hold'
    | 'return_at_risk' | 'recovery_blocked' | 'recovery_failed_swap'
    | 'dji_rth_takeover'
    // W8. The station is COVERED in both - the relief is holding it either way, from one of two
    // altitude slots that both sit inside the confirmation band. They say the rotation did not
    // finish cleanly, not that the mission is uncovered.
    | 'descent_deferred' | 'descent_failed'
    // #564's four. Unlike everything above, the station is UNCOVERED in all of them - a rotation
    // of one has nothing else in the air to carry it while its only aircraft is in trouble.
    | 'self_swap_recall_failed' | 'self_swap_recovery_blocked' | 'self_swap_recovery_failed'
    | 'self_swap_station_unconfirmed'
    // #569's one, and the only state on this frame that reports nothing wrong. The station was
    // stood down while the cycle was in the air - the coverage window ended, or the rotation was
    // suspended - so the aircraft came home, was resupplied, and is deliberately not going back up.
    | 'coverage_stood_down'
    // #703's two, and the only states here that report a command the platform sent with NOBODY
    // having approved it. The aircraft holding the station reached its own return point with no
    // relief coming, and was sent home rather than left to the airframe's own low-battery RTH -
    // which takes the flight controller and lands it on the GPS home point instead of the tray.
    // The station is uncovered from that moment. `_failed` is the same moment with the command
    // refused, so nothing is bringing it home at all.
    | 'last_resort_recall' | 'last_resort_recall_failed'
    | null;
  relief_drone_id: string | null;
  outgoing_drone_id: string | null;
}

interface WsMessage {
  type: string;
  data?:
    | LiveDrone
    | WsAlertPayload
    | CooperativeTraffic[]
    | CooperativeTraffic
    | LiveDockStatus
    | DockEventPayload
    | DockAckPayload
    | DroneAckPayload
    | DroneEventPayload
    | ManualControlStatePayload
    | CompanionStatus
    | MissionEventPayload
    | LaunchEventPayload
    | CoverageEventPayload
    | RotationEventPayload
    | PersonDetectedPayload
    | TriggerResolvedPayload
    | TriggerProposalPayload;
}

interface AlertEvent {
  severity: string;
  title: string;
}

/**
 * The authority words that mean nobody holds the aircraft from a COP any more —
 * the same set `services/manual-control-state.ts` calls TERMINAL_AUTHORITY.
 *
 * Everything else, INCLUDING a word this build has never heard of, counts as
 * held. The backend's Zod schema deliberately leaves the vocabulary open so a
 * newer RC build is not dropped wholesale, and the safe reading of an unknown
 * word is "somebody is flying it", never "it is free".
 */
const TERMINAL_MANUAL_AUTHORITY: ReadonlySet<string> = new Set(['released', 'override', 'fault']);

export interface WsAlertPayload {
  id: string;
  org_id: string;
  rule_id?: string | null;
  drone_id: string | null;
  severity: string;
  title: string;
  message: string;
  status: string;
  drone_callsign?: string;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  data?: WsAlertPayload;
}

export function useWebSocket(token: string | null) {
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [drones, setDrones] = useState<Map<string, LiveDrone>>(new Map());
  const [dockMap, setDockMap] = useState<Map<string, LiveDockStatus>>(new Map());
  // Companion-app liveness per drone (T4), from `companion_status` frames.
  // Only what has arrived on this connection — a screen opened before the first
  // heartbeat falls back to the registry row the REST payload carries, so the
  // live value always wins without needing to reconcile the two.
  const [companionMap, setCompanionMap] = useState<Map<string, CompanionStatus>>(new Map());
  const dockEventSubscribersRef = useRef<Set<(e: DockEventPayload) => void> | null>(null);
  if (dockEventSubscribersRef.current === null) dockEventSubscribersRef.current = new Set();
  const dockAckSubscribersRef = useRef<Set<(a: DockAckPayload) => void> | null>(null);
  if (dockAckSubscribersRef.current === null) dockAckSubscribersRef.current = new Set();
  const droneAckSubscribersRef = useRef<Set<(a: DroneAckPayload) => void> | null>(null);
  if (droneAckSubscribersRef.current === null) droneAckSubscribersRef.current = new Set();
  const droneEventSubscribersRef = useRef<Set<(e: DroneEventPayload) => void> | null>(null);
  if (droneEventSubscribersRef.current === null) droneEventSubscribersRef.current = new Set();
  // Manual flight authority, two ways, because two questions are being asked.
  //
  // The SUBSCRIPTION is for the session this console is flying: `useManualControl`
  // wants every frame as it lands, and routing 1 Hz dead-man ttls through React
  // state would re-render the whole COP for a value one hook reads.
  //
  // The MAP is for everybody else's sessions. `manual_control_state` is broadcast
  // org-wide, so this is the only way a second operator can learn that an
  // aircraft is already being flown by hand BEFORE they press MANUAL and spend a
  // round trip on a refusal. One entry per aircraft, at most one write per
  // second per live session, and it is dropped the moment the RC reports a
  // terminal authority — the cost of a re-render is the cost of not showing two
  // operators in one room the same air picture.
  const manualControlSubscribersRef = useRef<Set<(s: ManualControlStatePayload) => void> | null>(null);
  if (manualControlSubscribersRef.current === null) manualControlSubscribersRef.current = new Set();
  const [manualControlMap, setManualControlMap] = useState<Map<string, ManualControlHold>>(new Map());
  const missionEventSubscribersRef = useRef<Set<(e: MissionEventPayload) => void> | null>(null);
  if (missionEventSubscribersRef.current === null) missionEventSubscribersRef.current = new Set();

  const launchEventSubscribersRef = useRef<Set<(e: LaunchEventPayload) => void> | null>(null);
  if (launchEventSubscribersRef.current === null) launchEventSubscribersRef.current = new Set();

  const coverageEventSubscribersRef = useRef<Set<(e: CoverageEventPayload) => void> | null>(null);
  if (coverageEventSubscribersRef.current === null) coverageEventSubscribersRef.current = new Set();

  /**
   * The replacement forecast, on the COP's own socket.
   *
   * `RotationEventPayload` has been declared in this file, and listed in the frame union below,
   * since #250 W6 - but nothing here ever dispatched it, so the COP could see a station change
   * hands (`coverage_event`) and never see the swap that was about to do it. Approving one needs
   * an `action_id`, which rides this frame and `GET /api/coverage/:id/rotation`, and the COP read
   * neither.
   *
   * IT GOES ON THIS SOCKET RATHER THAN A SECOND ONE. `useCoverageEvents` also carries this frame,
   * but that hook exists to keep THIS hook off the mission page - a page with no alert surface and
   * a form under fleet-rate re-renders. The COP is the surface those behaviours were built for, so
   * mounting that hook here would buy nothing but a second connection on the one page that already
   * has one, and double the reconnect storms an operator sees on a flaky link.
   */
  const rotationEventSubscribersRef = useRef<Set<(e: RotationEventPayload) => void> | null>(null);
  if (rotationEventSubscribersRef.current === null) rotationEventSubscribersRef.current = new Set();

  const personDetectedSubscribersRef = useRef<Set<(e: PersonDetectedPayload) => void> | null>(null);
  if (personDetectedSubscribersRef.current === null) personDetectedSubscribersRef.current = new Set();

  const triggerResolvedSubscribersRef = useRef<Set<(e: TriggerResolvedPayload) => void> | null>(null);
  if (triggerResolvedSubscribersRef.current === null) triggerResolvedSubscribersRef.current = new Set();

  const triggerProposalSubscribersRef = useRef<Set<(e: TriggerProposalPayload) => void> | null>(null);
  if (triggerProposalSubscribersRef.current === null) triggerProposalSubscribersRef.current = new Set();
  const [cooperativeTraffic, setCooperativeTraffic] = useState<CooperativeTraffic[]>([]);
  const [prevToken, setPrevToken] = useState(token);
  const [lastAlert, setLastAlert] = useState<AlertEvent | null>(null);
  const alertSubscribersRef = useRef<Set<(a: WsAlertPayload) => void> | null>(null);
  if (alertSubscribersRef.current === null) alertSubscribersRef.current = new Set();
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const unmountingRef = useRef(false);

  const connectRef = useRef<() => void>(() => {});

  /** IEC 60601-1-8–style alarm patterns via Web Audio API */
  const playAlertTone = useCallback((kind: string) => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
      const ctx = audioCtxRef.current;
      void ctx.resume();

      const schedulePulse = (
        start: number,
        freqHz: number,
        durationSec: number,
        type: OscillatorType,
        peak: number,
      ) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.connect(g);
        g.connect(ctx.destination);
        osc.frequency.value = freqHz;
        osc.type = type;
        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(peak, start + 0.003);
        g.gain.setValueAtTime(peak, start + durationSec - 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, start + durationSec);
        osc.start(start);
        osc.stop(start + durationSec + 0.02);
      };

      const t0 = ctx.currentTime;
      const pulseOn = 0.1;
      const betweenPulses = 0.1;
      const step = pulseOn + betweenPulses;
      const groupPause = 0.2;

      if (kind === 'connection_lost') {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.connect(g);
        g.connect(ctx.destination);
        osc.frequency.value = 330;
        osc.type = 'sine';
        g.gain.setValueAtTime(0, t0);
        g.gain.linearRampToValueAtTime(0.12, t0 + 0.05);
        g.gain.setValueAtTime(0.12, t0 + 1.95);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 2);
        osc.start(t0);
        osc.stop(t0 + 2.05);
        return;
      }

      if (kind === 'critical') {
        // 3-2-3-2 at 880 Hz square, ~2.3 s
        let t = t0;
        const runGroup = (count: number) => {
          for (let i = 0; i < count; i += 1) {
            schedulePulse(t + i * step, 880, pulseOn, 'square', 0.18);
          }
          t += count * step - betweenPulses + groupPause;
        };
        runGroup(3);
        runGroup(2);
        runGroup(3);
        for (let i = 0; i < 2; i += 1) {
          schedulePulse(t + i * step, 880, pulseOn, 'square', 0.18);
        }
        return;
      }

      if (kind === 'warning') {
        for (let i = 0; i < 3; i += 1) {
          schedulePulse(t0 + i * step, 660, pulseOn, 'sine', 0.14);
        }
        return;
      }

      if (kind === 'info') {
        schedulePulse(t0, 440, 0.2, 'sine', 0.08);
        return;
      }

      // Unknown severity: treat as warning
      for (let i = 0; i < 3; i += 1) {
        schedulePulse(t0 + i * step, 660, pulseOn, 'sine', 0.14);
      }
    } catch {
      // Audio API not available
    }
  }, []);

  const connect = useCallback(() => {
    if (!token) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(`${getWsUrl()}?token=${token}`);
    } catch (e) {
      console.warn('WebSocket connection failed (mixed content?):', e);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttemptsRef.current = 0; // reset backoff on a healthy connection
    };

    ws.onmessage = (event) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);
        if (msg.type === 'telemetry' && msg.data) {
          const point = msg.data as LiveDrone;
          // The aircraft's own answer first, the 2 m bar as the fallback - the same rule the
          // server applies in `isPointAirborne` (src/lib/flight-altitude.ts), and it has to be
          // applied HERE too or the server-side fix never reaches the operator: this line
          // overwrites whatever `status` the server computed, on every frame.
          //
          // #338 is what that cost. A Mavic hovered at 1.9 m over its own pad for eight minutes
          // and this line called it `idle` the whole time, which the map paints BLUE for
          // on-ground while the aircraft was in the air in front of the operator.
          //
          // `=== true` and not truthiness: absent means the source cannot answer (most of them
          // cannot), never "no". `false` from an aircraft at altitude does not ground it either -
          // the bar still has the last word in that direction.
          point.status = (point.is_flying === true || point.altitude > 2) ? 'flying' : 'idle';
          point.last_seen_at = new Date().toISOString();

          setDrones((prev) => {
            const next = new Map(prev);
            next.set(point.drone_id, point);
            return next;
          });
        }

        if (msg.type === 'alert' && msg.data) {
          const raw = msg.data as unknown as WsAlertPayload;
          const alertData: WsAlertPayload = raw.data ?? raw;
          // The evaluator publishes on this channel twice per condition: once when it
          // raises, once when it returns to normal. The alarm list wants both — the
          // resolved row is what removes it live. The banner and the tone announce a
          // NEW condition, so a clear must not fire them.
          if (alertData.severity) {
            if (alertData.id) {
              alertSubscribersRef.current?.forEach((cb) => cb(alertData));
            }
            if (alertData.status !== 'resolved') {
              setLastAlert({ severity: alertData.severity, title: alertData.title });
              playAlertTone(alertData.severity);
            }
          }
        }

        if (msg.type === 'traffic' && Array.isArray(msg.data)) {
          setCooperativeTraffic(msg.data as CooperativeTraffic[]);
        }

        if (msg.type === 'traffic_update' && msg.data && typeof msg.data === 'object' && !Array.isArray(msg.data)) {
          const contact = msg.data as CooperativeTraffic;
          if (typeof contact.id === 'string') {
            setCooperativeTraffic((prev) => {
              const idx = prev.findIndex((t) => t.id === contact.id);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...prev[idx], ...contact };
                return next;
              }
              return [...prev, contact];
            });
          }
        }

        if (msg.type === 'dock_status' && msg.data) {
          const dock = msg.data as Partial<LiveDockStatus> & { dock_id?: string };
          if (dock.dock_id) {
            setDockMap((prev) => {
              const next = new Map(prev);
              const previous = prev.get(dock.dock_id as string);
              // The staleness sweep publishes a PARTIAL update ({dock_id, status,
              // is_online}); replacing wholesale would wipe the last-known
              // batteries/weather the COP labels as stale. Merge instead.
              next.set(dock.dock_id as string, { ...previous, ...dock } as LiveDockStatus);
              return next;
            });
          }
        }

        if (msg.type === 'dock_event' && msg.data) {
          const event = msg.data as DockEventPayload;
          dockEventSubscribersRef.current?.forEach((cb) => cb(event));
        }

        if (msg.type === 'dock_ack' && msg.data) {
          const ack = msg.data as DockAckPayload;
          if (ack.dock_id) {
            dockAckSubscribersRef.current?.forEach((cb) => cb(ack));
          }
        }

        // Coordination channel - drone flight-command acks and vehicle events
        // (forwarded verbatim by the server from org:{org}:coordination).
        if (msg.type === 'drone_ack' && msg.data) {
          const ack = msg.data as DroneAckPayload;
          if (ack.drone_id) {
            droneAckSubscribersRef.current?.forEach((cb) => cb(ack));
          }
        }

        // Companion-app health (T4). A distinct state from the aircraft's own:
        // the app can be alive with no aircraft bound, or dead while the
        // aircraft is flying on the sticks.
        if (msg.type === 'companion_status' && msg.data) {
          const companion = msg.data as CompanionStatus;
          if (companion.drone_id) {
            setCompanionMap((prev) => {
              const next = new Map(prev);
              next.set(companion.drone_id, companion);
              return next;
            });
          }
        }

        // What the RC is doing with manual authority — authority word, dead-man
        // ttl, envelope clamps, pilot override. Not telemetry: it must arrive
        // even when the aircraft is saying nothing, so it has its own channel
        // all the way from the broker (services/manual-control-state.ts).
        if (msg.type === 'manual_control_state' && msg.data) {
          const manualState = msg.data as ManualControlStatePayload;
          if (manualState.drone_id && manualState.session_id) {
            manualControlSubscribersRef.current?.forEach((cb) => cb(manualState));
            setManualControlMap((prev) => {
              const previous = prev.get(manualState.drone_id);
              if (TERMINAL_MANUAL_AUTHORITY.has(manualState.authority)) {
                // The session is over. Nothing is held, so nothing is claimed —
                // and a map that kept the last frame would go on telling the
                // rest of the org that an aircraft nobody is flying is taken.
                if (!previous) return prev;
                const next = new Map(prev);
                next.delete(manualState.drone_id);
                return next;
              }
              const at = Date.now();
              const next = new Map(prev);
              next.set(manualState.drone_id, {
                state: manualState,
                // A new session_id is a new session, so "since" restarts. The
                // same one carries its original first-seen forward, which is
                // what makes the refusal say when the holder actually took it
                // rather than when their last frame arrived.
                firstSeenAtMs:
                  previous && previous.state.session_id === manualState.session_id
                    ? previous.firstSeenAtMs
                    : at,
                lastSeenAtMs: at,
              });
              return next;
            });
          }
        }

        if (msg.type === 'drone_event' && msg.data) {
          const droneEvent = msg.data as DroneEventPayload;
          if (droneEvent.drone_id) {
            droneEventSubscribersRef.current?.forEach((cb) => cb(droneEvent));
          }
        }

        // Mission lifecycle (create / every status transition / delete), org-scoped by the
        // server. The dispatch board applies these to its rows; no state is kept here,
        // because the board is the surface that owns a mission list.
        if (msg.type === 'mission_event' && msg.data) {
          const missionEvent = msg.data as MissionEventPayload;
          if (missionEvent.mission_id) {
            missionEventSubscribersRef.current?.forEach((cb) => cb(missionEvent));
          }
        }

        // A dock launch moved to a new phase (#418). Org-scoped by the server. Events, not
        // state: the card that pressed LAUNCH owns the launch it is following, and a second
        // operator's card must not adopt it.
        if (msg.type === 'launch_event' && msg.data) {
          const launchEvent = msg.data as LaunchEventPayload;
          if (launchEvent.launch_id) {
            launchEventSubscribersRef.current?.forEach((cb) => cb(launchEvent));
          }
        }

        // Continuous coverage: the station was taken, lost, or changed hands. Org-scoped by the
        // server. No state kept here for the same reason as mission_event — the surfaces that
        // render coverage own their own rows.
        if (msg.type === 'coverage_event' && msg.data) {
          const coverageEvent = msg.data as CoverageEventPayload;
          if (coverageEvent.mission_id) {
            coverageEventSubscribersRef.current?.forEach((cb) => cb(coverageEvent));
          }
        }

        // The replacement forecast moved, or an operator approved/aborted one, or the orchestrator
        // reported where the relief has got to. Org-scoped by the server, and ADVISORY: nothing on
        // this frame has been commanded.
        //
        // NO FIELD BELOW `mission_id` IS READ HERE, deliberately. The five halves (`schedule`,
        // `safety`, `relief`, `approval`, `phase`) are independently nullable because they have
        // different producers on different clocks - the orchestrator broadcasts a phase-ONLY frame
        // with `schedule: null` - so a guard on any one of them would silently drop whole classes
        // of update. That exact trap has already cost this feature a PR: W3 declared the relief
        // nested inside the schedule while the server sent it at the top level, and the socket
        // carried no relief at all for a release. The subscriber decides what it needs.
        if (msg.type === 'rotation_event' && msg.data) {
          const rotationEvent = msg.data as RotationEventPayload;
          if (rotationEvent.mission_id) {
            rotationEventSubscribersRef.current?.forEach((cb) => cb(rotationEvent));
          }
        }

        // An external trigger fired. Events, not state - the surface that draws
        // the points owns the list, same reason as mission_event above. This
        // frame has been published by the server since the person-detection
        // ingest landed and, until now, nothing consumed it.
        if (msg.type === 'person_detected' && msg.data) {
          const detection = msg.data as PersonDetectedPayload;
          personDetectedSubscribersRef.current?.forEach((cb) => cb(detection));
        }

        // Somebody answered a trigger - possibly on another operator's COP.
        // Without this the point stays on a second screen and two aircraft get
        // sent to the same person.
        if (msg.type === 'trigger_resolved' && msg.data) {
          const resolved = msg.data as TriggerResolvedPayload;
          if (resolved.id) {
            triggerResolvedSubscribersRef.current?.forEach((cb) => cb(resolved));
          }
        }

        // The platform named an aircraft for a trigger, or withdrew one it had named. Both go to
        // one subscriber: the card that draws a proposal is the same card that has to stop
        // drawing it, and splitting them would let a surface handle one and not the other.
        if ((msg.type === 'trigger_proposed' || msg.type === 'trigger_proposal_voided') && msg.data) {
          const row = msg.data as Omit<TriggerProposalPayload, 'kind'>;
          if (row.id) {
            const payload: TriggerProposalPayload = {
              ...row,
              kind: msg.type === 'trigger_proposed' ? 'proposed' : 'voided',
            };
            triggerProposalSubscribersRef.current?.forEach((cb) => cb(payload));
          }
        }
      } catch (err) {
        // Malformed frame - never silently swallow; log with context so a
        // server/protocol drift is visible instead of telemetry just vanishing.
        console.warn('[ws] dropped malformed message:', err, typeof event.data === 'string' ? event.data.slice(0, 200) : event.data);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Don't reconnect after unmount or logout (token cleared) - otherwise the
      // close() fired during cleanup would schedule a zombie reconnect.
      if (unmountingRef.current || !token) return;
      // Exponential backoff (3s → 6s → 12s → 24s, capped at 30s) so a flapping
      // or down server isn't hammered; reset to 3s on the next healthy open.
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(30_000, 3_000 * 2 ** attempt);
      reconnectAttemptsRef.current = attempt + 1;
      console.warn(`[ws] connection lost - reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`);
      reconnectTimeout.current = setTimeout(() => connectRef.current(), delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token, playAlertTone]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    unmountingRef.current = false;
    connect();
    return () => {
      unmountingRef.current = true;
      clearTimeout(reconnectTimeout.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // Clear cooperative traffic when the user logs out (token cleared). Adjusting
  // state during render with a prev-prop comparison avoids the extra commit an
  // effect would introduce.
  if (token !== prevToken) {
    setPrevToken(token);
    if (!token) {
      setCooperativeTraffic([]);
    }
  }

  useEffect(() => {
    if (!token) return;
    api
      .get<{ data: CooperativeTraffic[] }>('/api/traffic')
      .then((r) => setCooperativeTraffic(r.data))
      .catch((err) => console.warn('[traffic] initial cooperative-traffic fetch failed:', err));
  }, [token]);

  const dismissLastAlert = useCallback(() => {
    setLastAlert(null);
  }, []);

  const subscribeFullAlert = useCallback((cb: (a: WsAlertPayload) => void) => {
    const set = alertSubscribersRef.current;
    set?.add(cb);
    return () => {
      set?.delete(cb);
    };
  }, []);

  const subscribeDockEvent = useCallback((cb: (e: DockEventPayload) => void) => {
    const set = dockEventSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeDockAck = useCallback((cb: (a: DockAckPayload) => void) => {
    const set = dockAckSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeDroneAck = useCallback((cb: (a: DroneAckPayload) => void) => {
    const set = droneAckSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeDroneEvent = useCallback((cb: (e: DroneEventPayload) => void) => {
    const set = droneEventSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeManualControlState = useCallback((cb: (s: ManualControlStatePayload) => void) => {
    const set = manualControlSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeMissionEvent = useCallback((cb: (e: MissionEventPayload) => void) => {
    const set = missionEventSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeLaunchEvent = useCallback((cb: (e: LaunchEventPayload) => void) => {
    const set = launchEventSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeCoverageEvent = useCallback((cb: (e: CoverageEventPayload) => void) => {
    const set = coverageEventSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeRotationEvent = useCallback((cb: (e: RotationEventPayload) => void) => {
    const set = rotationEventSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribePersonDetected = useCallback((cb: (e: PersonDetectedPayload) => void) => {
    const set = personDetectedSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeTriggerResolved = useCallback((cb: (e: TriggerResolvedPayload) => void) => {
    const set = triggerResolvedSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const subscribeTriggerProposal = useCallback((cb: (e: TriggerProposalPayload) => void) => {
    const set = triggerProposalSubscribersRef.current;
    set?.add(cb);
    return () => { set?.delete(cb); };
  }, []);

  const dronesArray = Array.from(drones.values());
  const docksArray = Array.from(dockMap.values());

  return { connected, drones: dronesArray, droneMap: drones, docks: docksArray, dockMap, companionMap, manualControlMap, cooperativeTraffic, lastAlert, dismissLastAlert, subscribeFullAlert, subscribeDockEvent, subscribeDockAck, subscribeDroneAck, subscribeDroneEvent, subscribeManualControlState, subscribeMissionEvent, subscribeLaunchEvent, subscribeCoverageEvent, subscribeRotationEvent, subscribePersonDetected, subscribeTriggerResolved, subscribeTriggerProposal };
}
